import { ImageFormType, ImageResponseType } from '@web-apps/utils-types'

type ImageUrlBuilderProps = {
  image?: ImageResponseType | ImageFormType | string
  width?: number
  height?: number
}

type ImageLoaderProps = {
  src: string
  width?: number
  height?: number
}

export const isImageOptimized = (image: ImageResponseType | ImageFormType) => {
  return isZezamOptimizedImage(image) || isImgproxyImage(image)
}

const isZezamOptimizedImage = (image: ImageResponseType | ImageFormType) =>
  image.type &&
  image.type.startsWith('image/') &&
  image.href &&
  image.href?.startsWith('https://media.zezam')

const isImgproxyImage = (image: ImageResponseType | ImageFormType) =>
  image.key && image.base_url

export const imageUrlBuilder = ({
  image,
  width,
  height,
}: ImageUrlBuilderProps) => {
  if (!image) {
    return undefined
  }

  if (typeof image === 'string') {
    if (image.startsWith('https://media.zezam')) {
      return buildImageUrl({ src: image, width, height })
    } else {
      return image
    }
  }

  if (isZezamOptimizedImage(image)) {
    return buildImageUrl({ src: image.href as string, width, height })
  }

  if (isImgproxyImage(image)) {
    return buildImageUrl({
      src: new URL(image.key as string, image.base_url).href,
      width,
      height,
    })
  }

  return image.href
}

/**
 * @see https://docs.metapic.dev/engineering/image-processing/
 */
const buildImageUrl = ({ src, width, height }: ImageLoaderProps) => {
  const url = new URL(src)
  const resize = ['rs', 'fit', width, height].filter(Boolean).join(':')
  const processing = [resize, 'g:sm', 'q:90'].join('/')
  const resource = 'zezam/' + url.pathname.slice(1)
  const resourceEncoded = Buffer.from(resource).toString('base64')
  const imgproxyHost = mapLegacyImageHostToImgproxy(url.origin)
  const signature = '_'
  return [imgproxyHost, signature, processing, resourceEncoded].join('/')
}

/**
 * @see https://metapic.atlassian.net/browse/MTPC-1477
 */
const mapLegacyImageHostToImgproxy = (src: string) => {
  switch (src) {
    case 'https://media.zezam.xyz':
      return 'https://media.metapic.dev'
    case 'https://media.zezam.io':
      return 'https://media.metapic.com'
    default:
      return src
  }
}

/**
 * @see https://nextjs.org/docs/13/app/api-reference/components/image#required-props
 */
export const imageLoader = ({ src, width, height }: ImageLoaderProps) => {
  const link = buildImageUrl({ src, width, height })

  // The width parameter is needed to skip next js warning
  return `${link}?=${width || height}`
}
